import { useEffect } from "react";

type SavedDocumentScrollState = {
  documentOverflow: string;
  documentOverflowX: string;
  documentOverflowY: string;
  documentOverscrollBehavior: string;
  bodyOverflow: string;
  bodyOverflowX: string;
  bodyOverflowY: string;
  bodyOverscrollBehavior: string;
  bodyPaddingRight: string;
};

let documentModalLockCount = 0;
let savedDocumentScrollState: SavedDocumentScrollState | null = null;

function acquireDocumentModalScrollLock() {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return;
  }

  documentModalLockCount += 1;
  if (documentModalLockCount > 1) return;

  const { body, documentElement } = document;
  savedDocumentScrollState = {
    documentOverflow: documentElement.style.overflow,
    documentOverflowX: documentElement.style.overflowX,
    documentOverflowY: documentElement.style.overflowY,
    documentOverscrollBehavior: documentElement.style.overscrollBehavior,
    bodyOverflow: body.style.overflow,
    bodyOverflowX: body.style.overflowX,
    bodyOverflowY: body.style.overflowY,
    bodyOverscrollBehavior: body.style.overscrollBehavior,
    bodyPaddingRight: body.style.paddingRight,
  };

  // Measure the actual layout width before and after locking. This respects
  // the app's existing scrollbar-gutter rules and only compensates if hiding
  // the document scrollbar releases horizontal space in this browser.
  const clientWidthBeforeLock = documentElement.clientWidth;

  documentElement.dataset.modalScrollLocked = "true";
  body.dataset.modalScrollLocked = "true";

  documentElement.style.overflow = "hidden";
  documentElement.style.overflowX = "hidden";
  documentElement.style.overflowY = "hidden";
  documentElement.style.overscrollBehavior = "none";
  body.style.overflow = "hidden";
  body.style.overflowX = "hidden";
  body.style.overflowY = "hidden";
  body.style.overscrollBehavior = "none";

  const releasedScrollbarSpace = Math.max(
    0,
    documentElement.clientWidth - clientWidthBeforeLock,
  );
  if (releasedScrollbarSpace > 0) {
    const currentPaddingRight = Number.parseFloat(
      window.getComputedStyle(body).paddingRight,
    );
    body.style.paddingRight = `${
      (Number.isFinite(currentPaddingRight) ? currentPaddingRight : 0) +
      releasedScrollbarSpace
    }px`;
  }
}

function releaseDocumentModalScrollLock() {
  if (typeof document === "undefined") return;
  if (documentModalLockCount === 0) return;

  documentModalLockCount -= 1;
  if (documentModalLockCount > 0) return;

  const { body, documentElement } = document;
  const saved = savedDocumentScrollState;
  savedDocumentScrollState = null;

  delete documentElement.dataset.modalScrollLocked;
  delete body.dataset.modalScrollLocked;

  if (!saved) return;

  documentElement.style.overflow = saved.documentOverflow;
  documentElement.style.overflowX = saved.documentOverflowX;
  documentElement.style.overflowY = saved.documentOverflowY;
  documentElement.style.overscrollBehavior =
    saved.documentOverscrollBehavior;
  body.style.overflow = saved.bodyOverflow;
  body.style.overflowX = saved.bodyOverflowX;
  body.style.overflowY = saved.bodyOverflowY;
  body.style.overscrollBehavior = saved.bodyOverscrollBehavior;
  body.style.paddingRight = saved.bodyPaddingRight;
}

export function useDocumentModalScrollLock(locked: boolean) {
  useEffect(() => {
    if (!locked) return;

    acquireDocumentModalScrollLock();
    return releaseDocumentModalScrollLock;
  }, [locked]);
}
